import React, { useRef, useEffect, useState } from 'react';
import { Box, Spinner, Center, Text, IconButton, useColorModeValue } from '@chakra-ui/react';
import { FiPlay, FiPause } from 'react-icons/fi';
import IconWrapper from '../IconWrapper';

interface ShortVideoPlayerProps {
  videoUrl: string;
  posterUrl?: string;
  onVideoEnd?: () => void;
  onVideoError?: (error: string) => void;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
}

const ShortVideoPlayer: React.FC<ShortVideoPlayerProps> = ({
  videoUrl,
  posterUrl,
  onVideoEnd,
  onVideoError,
  autoPlay = true,
  loop = true,
  muted = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [showControls, setShowControls] = useState(false);
  
  const iconColor = useColorModeValue('white', 'white');
  const overlayBg = useColorModeValue('blackAlpha.300', 'blackAlpha.500');

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleError = () => {
      setIsLoading(false);
      setHasError(true);
      onVideoError?.('Failed to load video');
    };
    const handleEnded = () => onVideoEnd?.();
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [onVideoEnd, onVideoError]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.load();
  }, [videoUrl]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  };

  const handleVideoClick = () => {
    togglePlayPause();
  };

  const handleMouseEnter = () => {
    setShowControls(true);
  };

  const handleMouseLeave = () => {
    setShowControls(false);
  };



  if (hasError) {
    return (
      <Center h="100%" bg="gray.900" color="white">
        <Text>Failed to load video</Text>
      </Center>
    );
  }

  return (
    <Box 
      position="relative" 
      w="100%" 
      h="100%" 
      overflow="hidden"
      cursor="pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleVideoClick}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        poster={posterUrl}
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        playsInline
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center'
        }}
      />
      
      {/* Play/Pause Controls Overlay */}
      {(showControls || !isPlaying) && !isLoading && !hasError && (
        <Center
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg={overlayBg}
          transition="opacity 0.3s ease"
        >
          <IconButton
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
            icon={<IconWrapper icon={isPlaying ? FiPause : FiPlay} size={48} />}
            size="lg"
            variant="ghost"
            color={iconColor}
            _hover={{ bg: 'whiteAlpha.200' }}
            _active={{ bg: 'whiteAlpha.300' }}
            borderRadius="full"
            onClick={(e) => {
              e.stopPropagation();
              togglePlayPause();
            }}
          />
        </Center>
      )}
      
      {isLoading && (
        <Center
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(0, 0, 0, 0.5)"
        >
          <Spinner size="xl" color="white" />
        </Center>
      )}
    </Box>
  );
};

export default ShortVideoPlayer;
